import React from "react";

import psmdocParser from "../utils/psmdocParser";

const escapeHtml = (value: any) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");

const idFromString = (str: string) => {
  let id = String(str || "").trim();
  if (!id) {
    return "";
  }
  id = id.replace(" ", "-");
  id = id.replace(/[áäâà]/g, "a");
  id = id.replace(/[éëêè]/g, "e");
  id = id.replace(/[íïîì]/g, "i");
  id = id.replace(/[óöôò]/g, "o");
  id = id.replace(/[úüûù]/g, "u");
  id = id.replace(/[ñ]/g, "n");
  id = id.replace(/[ç]/g, "c");
  id = id.replace(/[ß]/g, "ss");
  id = id.replace(/[&]/g, "and");
  id = id.replace(/[^\w\s]/g, "");
  id = id.replace(/\s+/g, "-");
  id = id.replace(/^[^a-zA-Z0-9\-]+/, "");
  return id;
};

const buildPreviewMenu = (rawContent: string) => {
  const blocks = String(rawContent || "").split(/\r?\n\r?\n/);
  const links: string[] = [];
  const usedIds: string[] = [];

  blocks.forEach((block) => {
    const line = String(block || "").trim();
    if (!/^#H[1-6](\s|$)/i.test(line)) {
      return;
    }
    const level = Number(line.charAt(2));
    const firstSpace = line.indexOf(" ");
    const afterLevelId = firstSpace !== -1 ? line.substring(3, firstSpace).trim() : "";
    const title = firstSpace !== -1 ? line.substring(firstSpace + 1).trim() : "";
    let id = idFromString(afterLevelId || title);
    if (!id) {
      return;
    }
    if (usedIds.includes(id)) {
      let count = 1;
      while (usedIds.includes(`${id}${count}`)) {
        count += 1;
      }
      id = `${id}${count}`;
    }
    usedIds.push(id);
    links.push(
      `<li class=\"psm-menu-file psm-menu-l${level}\"><a href=\"#${id}\" data-doc-link=\"1\">${escapeHtml(
        title,
      )}</a></li>`,
    );
  });

  if (!links.length) {
    return '<ul class="psm-menu-level"><li class="psm-menu-file"><a href="#psm-main-content" data-doc-link="1">Contenido</a></li></ul>';
  }
  return `<ul class="psm-menu-level">${links.join("")}</ul>`;
};

const previewInlineScript = `
(function () {
  var root = document.documentElement;
  var body = document.body;
  var themeKey = "psm-theme";
  var contrastKey = "psm-contrast";
  var scaleKey = "psm-font-scale";

  function applyTheme(theme) {
    var resolved = theme;
    if (theme === "auto") {
      var prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
      resolved = prefersDark ? "dark" : "light";
    }
    root.setAttribute("data-psm-theme", resolved === "dark" ? "dark" : "light");
    var toggle = document.getElementById("psm-theme-toggle");
    if (toggle) {
      toggle.setAttribute("aria-pressed", resolved === "dark" ? "true" : "false");
      toggle.textContent = resolved === "dark" ? "Claro" : "Oscuro";
    }
  }

  function applyContrast(enabled) {
    root.classList.toggle("psm-high-contrast", enabled);
    var toggle = document.getElementById("psm-contrast-toggle");
    if (toggle) {
      toggle.setAttribute("aria-pressed", enabled ? "true" : "false");
    }
  }

  function clampScale(value) {
    return Math.max(0.9, Math.min(1.4, value));
  }

  function applyScale(value) {
    var next = clampScale(value);
    root.style.setProperty("--psm-font-scale", String(next));
    localStorage.setItem(scaleKey, String(next));
    return next;
  }

  document.addEventListener("DOMContentLoaded", function () {
    var theme = localStorage.getItem(themeKey) || "auto";
    var contrast = localStorage.getItem(contrastKey) === "high";
    var scale = parseFloat(localStorage.getItem(scaleKey) || "1");
    var menuToggle = document.getElementById("psm-mobile-nav-toggle");

    applyTheme(theme);
    applyContrast(contrast);
    scale = applyScale(isNaN(scale) ? 1 : scale);

    var themeToggle = document.getElementById("psm-theme-toggle");
    if (themeToggle) {
      themeToggle.addEventListener("click", function () {
        theme = (localStorage.getItem(themeKey) || "auto") === "dark" ? "light" : "dark";
        localStorage.setItem(themeKey, theme);
        applyTheme(theme);
      });
    }

    var contrastToggle = document.getElementById("psm-contrast-toggle");
    if (contrastToggle) {
      contrastToggle.addEventListener("click", function () {
        contrast = !root.classList.contains("psm-high-contrast");
        localStorage.setItem(contrastKey, contrast ? "high" : "normal");
        applyContrast(contrast);
      });
    }

    var inc = document.getElementById("psm-font-increase");
    var dec = document.getElementById("psm-font-decrease");
    var reset = document.getElementById("psm-font-reset");
    if (inc) {
      inc.addEventListener("click", function () {
        scale = applyScale(scale + 0.08);
      });
    }
    if (dec) {
      dec.addEventListener("click", function () {
        scale = applyScale(scale - 0.08);
      });
    }
    if (reset) {
      reset.addEventListener("click", function () {
        scale = applyScale(1);
      });
    }

    if (menuToggle) {
      menuToggle.addEventListener("click", function () {
        var open = !body.classList.contains("psm-sidebar-open");
        body.classList.toggle("psm-sidebar-open", open);
        menuToggle.setAttribute("aria-expanded", open ? "true" : "false");
      });
    }
  });
})();
`;

const extractHeaderMeta = (rawContent: string) => {
  const lines = String(rawContent || "").split(/\r?\n/);
  let firstLineIndex = 0;
  while (firstLineIndex < lines.length && lines[firstLineIndex].trim() === "") {
    firstLineIndex += 1;
  }
  const firstLine = lines[firstLineIndex] || "";
  if (!firstLine.trim().startsWith("##")) {
    return {
      title: null as string | null,
      label: null as string | null,
      content: rawContent,
    };
  }

  const header = firstLine.replace(/^##\s*/, "");
  const meta: { [key: string]: string } = {};
  const regex = /(\w+)\s*=\s*"([^"]*)"|(\w+)\s*=\s*'([^']*)'/g;
  let match;
  while ((match = regex.exec(header))) {
    const key = match[1] || match[3];
    const value = match[2] || match[4] || "";
    if (key) {
      meta[key] = value;
    }
  }

  const remainingLines = lines.slice(firstLineIndex + 1).join("\n");
  return {
    title: meta.title || null,
    label: meta.label || null,
    content: remainingLines,
  };
};

const psmdocPreview = ({
  content,
  title,
  projectConfig,
}: {
  content: string;
  title: string;
  projectConfig?: Record<string, any>;
}) => {
  const headerMeta = extractHeaderMeta(content || "");
  const displayTitle = headerMeta.title || title;
  const displayLabel = headerMeta.label || displayTitle;
  const config = projectConfig || {};
  const metaTitle = escapeHtml(config.title || displayTitle);
  const metaDescription = escapeHtml(config.description || "");
  const metaKeywords = escapeHtml(config.keywords || "");
  const metaAuthor = escapeHtml(config.author || "");
  const metaRobots = escapeHtml(config.robots || "");
  const fontUrl = escapeHtml(config.font || "");
  const safeDisplayTitle = escapeHtml(displayTitle || "Proyecto");
  const menuHtml = buildPreviewMenu(headerMeta.content || "");
  const styleConfig = config.style || {};
  const bodyStyle = Object.entries({
    background: styleConfig.background,
    color: styleConfig.color,
    "font-family": styleConfig["font-family"],
    "font-size": styleConfig["font-size"],
    "line-height": styleConfig["line-height"],
    "text-align": styleConfig["text-align"],
  })
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    )
    .map(([key, value]) => `${key}:${escapeHtml(value)}`)
    .join(";");
  return `
        <!DOCTYPE html>
  <html lang="en">
  <head>
    <meta charset="UTF-8">
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${metaTitle}</title>
    <meta name="description" content="${metaDescription}">
    <meta name="keywords" content="${metaKeywords}">
    <meta name="author" content="${metaAuthor}">
    <meta name="robots" content="${metaRobots}">
    ${fontUrl ? `<link rel="stylesheet" href="${fontUrl}" />` : ""}
    <link rel="stylesheet" href="/djstatic/css/psmdoc.css">
    <link rel="stylesheet" href="https://psmdoc.gestionciudad.com/djstatic/css/psmdoc.css">
    <style>
      /* Fallback minimo para evitar preview "sin estilo" si falla la carga externa */
      .psm-topbar { display:flex; align-items:center; gap:8px; padding:10px 12px; border-bottom:1px solid #d9e2ef; background:#fff; }
      .psm-layout { display:flex; min-height:560px; }
      .psm-sidebar { width:300px; max-width:35%; overflow:auto; border-right:1px solid #d9e2ef; padding:12px; background:#f7f9fc; }
      .psm-content { flex:1; overflow:auto; padding:16px; }
      .psm-content-body { max-width:980px; margin:0 auto; }
      .psm-footer { border-top:1px solid #d9e2ef; padding:10px 14px; text-align:center; color:#4a5c73; background:#fff; }
      .psm-toolbar { margin-left:auto; display:inline-flex; gap:6px; }
      .psm-version-wrap { margin-left:8px; }
      .psm-menu-level { margin:0; padding-left:14px; }
      .psm-mobile-nav-toggle { display:none; }
    </style>
  </head>
    <body style="${bodyStyle}">
      <a class="psm-skip-link" href="#psm-main-content">Saltar al contenido principal</a>
      <header class="psm-topbar" id="top">
        <button class="psm-mobile-nav-toggle" id="psm-mobile-nav-toggle" aria-controls="psm-sidebar" aria-expanded="false" type="button">Menu</button>
        <a class="psm-brand" href="#top">
          <div class="psm-logo"></div>
          <span class="psm-project-title">${safeDisplayTitle}</span>
          <span class="psm-project-version">v1.0.0</span>
        </a>
        <div class="psm-toolbar" aria-label="Herramientas de accesibilidad">
          <button class="psm-toolbar-btn" id="psm-font-decrease" type="button" aria-label="Disminuir tamaño de texto">A-</button>
          <button class="psm-toolbar-btn" id="psm-font-reset" type="button" aria-label="Restablecer tamaño de texto">A</button>
          <button class="psm-toolbar-btn" id="psm-font-increase" type="button" aria-label="Aumentar tamaño de texto">A+</button>
          <button class="psm-toolbar-btn" id="psm-theme-toggle" type="button" aria-pressed="false" aria-label="Cambiar tema claro u oscuro">Tema</button>
          <button class="psm-toolbar-btn" id="psm-contrast-toggle" type="button" aria-pressed="false" aria-label="Activar alto contraste">Contraste</button>
        </div>
        <label class="psm-version-wrap" for="psm-version-select">Version</label>
        <select class="psm-version-select" id="psm-version-select" data-project-path="/preview/">
          <option value="1.0.0" selected>1.0.0</option>
        </select>
      </header>
      <div class="psm-layout">
        <aside class="psm-sidebar" id="psm-sidebar">
          <h2>${escapeHtml(displayLabel)}</h2>
          ${menuHtml}
        </aside>
        <main class="psm-content" id="psm-main-content">
          <div class="psm-content-body">
            ${psmdocParser(headerMeta.content)}
          </div>
        </main>
      </div>
      <footer class="psm-footer" id="footer">
        <p>&copy; ${safeDisplayTitle} ${new Date().getFullYear()}</p>
      </footer>
    </body>
    <script>${previewInlineScript}</script>
    <script src="/djstatic/js/psmdoc.js"></script>
    <script src="https://psmdoc.gestionciudad.com/djstatic/js/psmdoc.js"></script>
  </html>`;
};

export default psmdocPreview;
